import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Button, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import { useAppDispatch } from 'store/hooks';
import { setTour } from 'store/tour/slice';
import {
  LegacyAppointment,
  createRoom,
  fetchCreatorAppointments,
  openRoom as openRoomRest
} from 'services/api/appointments';

import { PageWrapper } from 'components/ui';
import useCopyText from 'hooks/useCopyText';
import { showRestError } from 'utils/showErrorMessage';
import { getIdStart } from 'utils/string';
import { CreateVideoCall } from './components';
import { HowItWorksLink } from 'components/shared';

const PAGE_LIMIT = 10;

const { Title, Paragraph } = Typography;

const VideoCall: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { copyText } = useCopyText();
  const [links, setLinks] = useState<Record<string, string>>({});
  const [passwords, setPasswords] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  const [results, setResults] = useState<LegacyAppointment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetchCreatorAppointments({ page, limit: PAGE_LIMIT })
      .then(res => {
        setResults(res.results);
        setTotal(res.total);
        dispatch(setTour({ key: 'videoCall', value: true }));
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [page, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = () => {
    setPage(1);
    load();
  };

  const onCopyRoom = useCallback(
    async (record: LegacyAppointment) => {
      // link/password state is keyed by the row id (the columns look it up);
      // the meeting calls + shared URL key off the public hashId when present
      const id = String(record.id);
      const meetingId = record.hashId ?? id;

      try {
        const room = await openRoomRest(meetingId);

        if (room.redirect_url) {
          const roomHash = record.hashId || room.meetUrl;
          const link = `${room.redirect_url}/home/${roomHash}?creator=doctor`;

          setLinks(prev => ({ ...prev, [id]: link }));
          copyText(link);

          const created = await createRoom(meetingId);

          setPasswords(prev => ({ ...prev, [id]: created.password }));
        }
      } catch (err) {
        showRestError(err);
      }
    },
    [copyText]
  );

  const columns: ColumnProps<LegacyAppointment>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: id => getIdStart(id)
      },
      {
        title: 'hashId',
        dataIndex: 'hashId',
        key: 'hashId',
        render: hashId => getIdStart(hashId)
      },
      {
        title: t('sessions.note'),
        dataIndex: 'notes',
        key: 'notes',
        render(value) {
          return <p style={{ maxWidth: 200 }}>{value?.[0]?.note || '-'}</p>;
        }
      },
      {
        title: t('sessions.link'),
        dataIndex: 'id',
        key: 'link',
        width: '30%',
        render: id =>
          links[id] ? (
            <Paragraph
              data-element="video-call-link"
              style={{ whiteSpace: 'nowrap' }}
              copyable={{ text: links[id] }}
            >
              {`${links[id].slice(0, 40)}...`}
            </Paragraph>
          ) : (
            '-'
          )
      },
      {
        title: t('sessions.password'),
        dataIndex: 'id',
        key: 'password',
        render: id =>
          passwords[id] ? <Paragraph copyable>{passwords[id]}</Paragraph> : '-'
      },
      {
        title: t('sessions.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: '25%',
        render: createdAt => dayjs(createdAt).format(FULL_DATE_FORMAT)
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'actions',
        align: 'right',
        render: (id, record) => (
          <Button
            data-element="video-call-generate-button"
            type="default"
            size="middle"
            onClick={() => onCopyRoom(record)}
          >
            {t('sessions.password_and_link')}
          </Button>
        )
      }
    ],
    [t, links, passwords, onCopyRoom]
  );

  return (
    <PageWrapper
      title={t('video_call.title')}
      color="white"
      extra={[
        <HowItWorksLink
          key="how-it-works"
          type={DOCTOR_HOW_IT_WORKS_PAGES.VIDEO_CALL.key}
          title={t('common.how_it_works')}
        />
      ]}
    >
      <CreateVideoCall onCreate={onCreate} />
      <Title level={3}>{t('video_call.previous_video_call_link')}</Title>
      {/* <Space size={16} style={{ marginBottom: 16 }}>
        <Input.Search
          size="large"
          style={{ width: 289 }}
          placeholder={t('video_call.search_by_id')}
        />
        <Space size={8}>
          <Button type="primary" size="large">
            {t('common.apply')}
          </Button>
          <Button type="default" size="large">
            {t('common.reset')}
          </Button>
        </Space>
      </Space> */}
      <Table
        dataSource={results}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          current: page,
          pageSize: PAGE_LIMIT,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
        scroll={{ x: 768 }}
      />
    </PageWrapper>
  );
};

export default VideoCall;
